import { useTranslation } from 'next-i18next';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useLockedBody } from 'usehooks-ts';

import { basketPageSlugs, colorTestProductSlugs, specialDecoTechniqueCategoryId } from '@boss/constants/b2b-b2c';
import { useIsSSR, useRouter } from '@boss/hooks';
import { IProduct } from '@boss/services';
import { ColorGroup, SimpleColor } from '@boss/types/b2b-b2c';
import { IArticle } from '@boss/types/b2b-b2c';
import { Alert, Button, ProductPanel } from '@boss/ui';

import { ArticlesTable, LegalGuaranteeLink, ProductDetailModals } from '..';
import LevermethodeSelection from './LevermethodeSelection';
import { useAccount } from '../../client-queries';
import { COLOR_TYPE, CONSUMPTION_UNIT } from '../../constants';
import { useLogin, usePdpStock, usePointOfSale, useProductData } from '../../hooks';
import { isB2b, productPanelVariant, stockConfig } from '../../utils';
import { ArticleIdsObject } from '../ArticlesTable';
import ArticlesTableSkeleton from '../ArticlesTable/Skeleton';
import { Modal } from '../Modals/ProductDetailModals';

type Props = {
  hasArticles: boolean;
  addToBasket: (params: { callback?: () => void; isColorProduct: boolean }) => void;
  className?: string;
  colorGroups: ColorGroup[] | null;
  colorType: 'transparant' | 'default';
  hasError: boolean;
  price?: number;
  product: IProduct;
  selectedArticleIdsObject: ArticleIdsObject;
  selectedColor?: SimpleColor;
  setHasError: (bool: boolean) => void;
  setInfoMessage: (message: { title: string; message: string } | undefined) => void;
  setSelectedArticleIdsObject: Dispatch<SetStateAction<ArticleIdsObject>>;
  setSelectedColor: (color: SimpleColor | undefined) => void;
  showProductActions?: boolean;
  showBaseColors?: boolean;
  // [39787] RBAC permissions from useContactPermissions (B2B only). Default true
  // so colora/B2C (undefined permissions) renders exactly as before.
  showprice?: boolean;
  canorder?: boolean;
  hasArticlesSelected: boolean;
  setArticles: Dispatch<SetStateAction<IArticle[]>>;
  isBasketUpdateLoading?: boolean;
  infoMessage?: {
    title: string;
    message: string;
  };
};

// TODO: Refactor this component to be more readable
const ProductPanelWrapper = ({
  addToBasket,
  className,
  colorGroups,
  colorType,
  hasError,
  price,
  product,
  selectedArticleIdsObject,
  selectedColor,
  setHasError,
  setInfoMessage,
  setSelectedArticleIdsObject,
  setSelectedColor,
  showProductActions,
  showBaseColors,
  showprice = true,
  canorder = true,
  hasArticlesSelected,
  setArticles,
  hasArticles,
  isBasketUpdateLoading,
  infoMessage,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
Props) => {
  const isSSR = useIsSSR();
  const { handleLogin, isLoggedIn, handleRegister } = useLogin();
  const { t } = useTranslation(['product', 'stores', 'common']);
  const { asPath, locale, isReady, query } = useRouter();
  const isSamplePage = asPath.startsWith(colorTestProductSlugs[locale]);

  const [activeModal, setActiveModal] = useState<Modal | undefined>();
  const legalGuaranteeLinkRef = useRef<HTMLButtonElement>(null);
  const openSurfaceCalculatorModal = () => setActiveModal('surfaceCalculator');
  const openLegalGuaranteeModal = () => setActiveModal('legalGuarantee');

  const isPaint = product.type !== 'NON_PAINT';
  const isSpecialDecoTechnique = product.categories.some(category => category.id === specialDecoTechniqueCategoryId);
  const enableSurfaceCalculator = isReady && !!product.consumptionOptions?.length && isPaint && !isSamplePage;

  /**
   * This is about as good as it gets without restructering and rethinking the whole PDP functionality
   */
  const {
    favoriteColors,
    colorsByGroup,
    searchColors,
    searchLoading,
    activeProductOption,
    setSelectedColorGroup,
    productOptions,
    selectedColorGroup,
    handleProductOptionClick,
    setSearchedValue,
    searchValue,
    addColor,
    calculated,
    searchedValue,
    setCalculated,
    setSearchValue,
    viewedColors,
    showPrivatePrices,
    showPrice,
    showColors,
    handleTogglePrivatePrices,
    articles,
    articlesLoading,
    preSelectedColor,
    preSelectedColorIsLoading,
    setSelectedColorPerGroup,
    selectedColorPerGroup,
    noArticlesAvailable,
  } = useProductData({
    showBaseColors: !!showBaseColors,
    isPaint,
    productId: product.id,
    selectedArticleIdsObject,
    selectedColor,
    hasArticles,
    colorTypes: product.colortypegroups,
  });

  const enableColors = showColors && isPaint && !!productOptions?.length;

  const { stores } = usePointOfSale(3);
  const { data: account } = useAccount(locale);

  // [46231] sv04 channel stock gate, previously the useStock wrapper inside
  // useProductData ('auth' = logged-in only). When the channel hides stock,
  // neither the levermethode selector nor the inline statuses render.
  const showStock = stockConfig.visible === 'auth' ? isLoggedIn : stockConfig.visible;

  const { levermethode, setLevermethode, afhaalpuntId, setAfhaalpuntId, favouriteShop, statusByArticleId } =
    usePdpStock({
      articles: articles ?? [],
      favouriteDelivery: account?.preference?.delivery ?? null,
      favouriteShopId: account?.preference?.shop ?? null,
      locale,
      selectedArticleIdsObject,
      stores,
    });

  const openColorModal = () => {
    setActiveModal('color');
  };

  const handleProductPanelSearch = () => {
    setSearchedValue(searchValue);

    openColorModal();
  };

  const handleColorGroupClick = (color?: SimpleColor) => {
    if (color) {
      setSelectedColorGroup(color);
    }
    openColorModal();
  };

  const onColorClick = (simpleColor: SimpleColor) => {
    _setSelectedColor(simpleColor);
    addColor(simpleColor);
    setSelectedArticleIdsObject({});
    setAmountOfPaintNeeded(0);
  };

  useEffect(() => {
    if (articles?.length) {
      setArticles(articles);
    }
  }, [articles, setArticles]);

  const _setSelectedColor = (color: SimpleColor, colorBase?: string) => {
    if (colorBase) {
      handleProductOptionClick({
        key: colorBase,
        value: colorBase,
      });
    }

    setAmountOfPaintNeeded(0);
    if (activeProductOption?.key) {
      setSelectedColorPerGroup({
        ...selectedColorPerGroup,
        [activeProductOption.key]: color,
      });
    }
    setSelectedColor(color);
  };

  const setProductOption = (option: { key: string; value: string }) => {
    const color = selectedColorPerGroup[option.key];

    setSelectedColor(color ?? undefined);

    handleProductOptionClick({
      key: option.key as COLOR_TYPE,
      value: option.value,
    });
  };

  useEffect(() => {
    if (preSelectedColor) {
      _setSelectedColor(preSelectedColor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedColor]);

  const isPanelLoading = isSSR || searchLoading || preSelectedColorIsLoading || !isReady;

  const handleAddToBasket = async () => {
    addToBasket({
      callback: !isB2b && product.crossSell?.length ? () => setActiveModal('crossSelling') : undefined,
      isColorProduct: enableColors,
    });
  };

  useEffect(() => {
    if (query.colorId && query.colorGroupType && colorsByGroup?.length && isReady) {
      const color = colorsByGroup.find(color => color.colorid === query.colorId);

      if (color) {
        setSelectedColor({
          code: color.colorcode,
          name: color.colorname,
          rgb: color.rgb,
          id: color.colorid,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.colorId, query.colorGroupType, colorsByGroup, isReady]);

  useEffect(() => {
    if (!isB2b && !selectedColor && colorsByGroup?.length) {
      const color = colorsByGroup[0];

      setSelectedColor({
        code: color.colorcode,
        name: color.colorname,
        rgb: color.rgb,
        id: color.colorid,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor, isB2b, colorsByGroup]);

  const [amountOfPaintNeeded, setAmountOfPaintNeeded] = useState(0);

  useLockedBody(!!activeModal, 'root');

  const showWishlistModal = () => {
    if (hasArticlesSelected && (selectedColor || product.type === 'NON_PAINT')) {
      setActiveModal('wishlist');
      setInfoMessage(undefined);
    } else if (hasArticlesSelected && !selectedColor) {
      setInfoMessage({
        title: t('productPanel.infoTitle'),
        message: t('productPanel.infoMessage.selectColorForWishlistRequired'),
      });
    } else {
      setInfoMessage({
        title: t('productPanel.infoTitle'),
        message: t('productPanel.infoMessage.selectArticlesForWishlistRequired'),
      });
    }
  };

  return (
    <>
      {activeModal && (
        <ProductDetailModals
          activeModal={activeModal}
          articles={articles}
          calculated={calculated}
          colorGroups={colorGroups}
          favoriteColors={favoriteColors ?? []}
          legalGuaranteeReturnFocusRef={legalGuaranteeLinkRef}
          locale={locale}
          product={product}
          searchedValue={searchedValue}
          selectedArticleIdsObject={selectedArticleIdsObject}
          selectedColor={selectedColor}
          selectedColorGroup={selectedColorGroup}
          setActiveModal={setActiveModal}
          setAmountOfPaintNeeded={setAmountOfPaintNeeded}
          setCalculated={setCalculated}
          setSearchedValue={setSearchedValue}
          setSelectedArticleIdsObject={setSelectedArticleIdsObject}
          setSelectedColor={_setSelectedColor}
          setSelectedColorGroup={setSelectedColorGroup}
        />
      )}
      <ProductPanel
        activeProductOption={activeProductOption}
        addToBasket={handleAddToBasket}
        // [45976] hide the add-to-list action when ordering is not allowed for
        // the contact (also covers showprice=false && canorder=false)
        addToWishlist={canorder === false ? undefined : showWishlistModal}
        alternativeActions={
          <>
            <div>
              {/* ISO creating complicated business logic with a conditional default color, 
              simply removing duplicates yields the same result */}
              <p className="mb-4 font-bold">{t('alternativeActions.availableIn', { ns: 'product' })}</p>
              {[...new Set(product.articles?.map(article => article.description))].map(description => (
                <p
                  className="border-1 mr-4 inline-flex min-w-[3rem] items-center justify-center rounded-md border-gray-300 p-2 text-center align-middle"
                  key={description}
                >
                  {description}
                </p>
              ))}
            </div>
            <div className="bg-productPanel-alternativeActions-bg -mx-8 flex flex-col gap-4 px-8 py-8">
              <h4 className="text-center">{t('alternativeActions.title')}</h4>
              <Button
                className="w-full p-3 text-center"
                label={t('alternativeActions.register')}
                onClick={() => handleRegister(asPath)}
                type="primary"
              />
              <Button
                className="w-full p-3 text-center"
                label={t('alternativeActions.login')}
                onClick={() => handleLogin(asPath)}
                type="secondary"
              />
            </div>
          </>
        }
        calculatorResult={{
          value: amountOfPaintNeeded,
          unit: t(product.consumptionUnit === CONSUMPTION_UNIT.KILO ? 'kilo' : 'liter', { ns: 'common' }),
        }}
        className={className}
        colorOptions={colorsByGroup}
        colorType={colorType}
        colors={colorGroups}
        deliveryPromises={
          productPanelVariant === 'primary' ? (
            <LegalGuaranteeLink onClick={openLegalGuaranteeModal} ref={legalGuaranteeLinkRef} />
          ) : undefined
        }
        disclaimerLink={showPrice && showprice !== false && canorder !== false ? basketPageSlugs[locale] : ''}
        enableCalculator={enableSurfaceCalculator}
        enableColorPicker={enableColors}
        favoriteColors={favoriteColors}
        hasArticles={hasArticles}
        hasError={hasError}
        hasSearched={!!searchedValue}
        hideOrderAction={canorder === false}
        inStock
        infoMessage={infoMessage}
        isBasketUpdateLoading={isBasketUpdateLoading}
        isLoading={isPanelLoading}
        isLoggedIn={isLoggedIn}
        isSpecialDecoTechnique={isSpecialDecoTechnique}
        noArticlesAvailable={noArticlesAvailable}
        onCalculateSurface={openSurfaceCalculatorModal}
        onChangeGroupClick={handleColorGroupClick}
        onCloseAlert={() => setHasError(false)}
        onCloseInfo={() => setInfoMessage(undefined)}
        onColorClick={onColorClick}
        onProductOptionClick={setProductOption}
        onSearch={handleProductPanelSearch}
        openOrderForm={() => setActiveModal('specialForm')}
        // [45904] Same OR-rule as the footnote below: the "Nettoprijs (excl. BTW)"
        // row is only meaningful when prices are shown AND ordering is allowed —
        // with canorder=false the amount/eenheid selectors are hidden, so the
        // amount would always read € 0,00.
        price={showprice !== false && canorder !== false ? { value: price } : undefined}
        productOptions={productOptions}
        recentColors={viewedColors}
        searchColors={searchColors}
        searchLoading={searchLoading}
        searchValue={searchValue}
        selectedColor={selectedColor}
        setSearchValue={setSearchValue}
        showProductActions={showProductActions}
        table={
          /*
           * for some reason the internal loading state is not working properly
           */
          !articlesLoading ? (
            articles?.length ? (
              <>
                {/* [46231] sv04: the levermethode selector sits above the
                    articles table; the deliveryPromises footer slot keeps
                    only the legal guarantee link. */}
                {showStock && (
                  <LevermethodeSelection
                    afhaalpuntId={afhaalpuntId}
                    favouriteShopId={favouriteShop?.id ?? null}
                    levermethode={levermethode}
                    onAfhaalpuntChange={setAfhaalpuntId}
                    onLevermethodeChange={setLevermethode}
                    stores={stores}
                    variant={productPanelVariant}
                  />
                )}
                <ArticlesTable
                  articleType={isPaint ? 'paint' : 'nonPaint'}
                  articles={articles}
                  canorder={canorder}
                  isLoading={articlesLoading}
                  isLoggedIn={isLoggedIn}
                  selectedArticleIdsObject={selectedArticleIdsObject}
                  selectedColor={selectedColor}
                  setSelectedArticleIdsObject={setSelectedArticleIdsObject}
                  showPrivatePrices={showPrivatePrices}
                  showprice={showprice}
                  stockStatusByArticleId={showStock ? statusByArticleId : undefined}
                  togglePrivatePrices={productPanelVariant === 'secondary' ? handleTogglePrivatePrices : undefined}
                  type={showProductActions ? 'default' : 'slim'}
                />
              </>
            ) : (
              <Alert type="error">
                <div className="flex flex-col gap-2">
                  {t('productPanel.noArticles')}
                  <Button
                    className="text-black"
                    label={t('productPanel.selectOtherColor')}
                    onClick={() => openColorModal()}
                    type="link"
                  />
                </div>
              </Alert>
            )
          ) : (
            <ArticlesTableSkeleton />
          )
        }
        translations={{
          ...t('productPanel', { ns: 'product', returnObjects: true }),
          // [46231] the levermethode selector renders its own header; blank
          // the panel's generic "Bezorgopties" label so it is not duplicated
          // above the legal guarantee link in the footer.
          ...(productPanelVariant === 'primary' ? { deliveryOptions: '' } : {}),
        }}
        variant={productPanelVariant}
      />
    </>
  );
};

export default ProductPanelWrapper;
